import React, { useEffect, useState } from 'react';
import { Alert, Button, Collapse, Stack, Stepper } from '@mui/material';
import FileUpload from '../../../FileUpload';
import OperationDropdown from '../../../OperationDropdown';
import DevWarning from '../../../DevWarning';
import DeviceOwnerSelect from '../../../DeviceOwnerSelect';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import UploadReasonField from '../../../UploadReasonField';
import { removeOpSuffix } from '../../../../utils/operations';
import UseCaseSelector from '../../../UseCaseSelector';
import { getUploadComplete } from '../../../../store/upload/uploadSlice';
import { useUploadFormStepper } from '../UploadFormContext';
import { UploadFormStep } from './stepWrapper';
import { SnackbarAlert } from '../../../SnackbarAlert';
import { FeatureFlag } from '../../../../types/generic';
import { ArrowForward } from '@mui/icons-material';
import { switchTab } from '../../../../store/tab/tabSlice';

interface UploadSkeletonProps {
	allowedFormats: string[];
	inDev?: boolean;
	children: React.ReactNode;
	testTag?: string;
	noSuspect?: boolean;
}

const UploadTabSkeleton: React.FC<UploadSkeletonProps> = ({ allowedFormats, inDev, children, testTag, noSuspect }) => {
	const dispatch = useAppDispatch();

	const featureFlags = useAppSelector((state) => state.tab.featureFlags);
	const [selectedOperation, setSelectedOperation] = useState<string>('');
	const [loadError, setLoadError] = useState<string>('');
	const savedSuspect = useAppSelector((state) => state.upload.deviceOwner);
	const suspectType = useAppSelector((state) => state.upload.deviceOwnerType);
	const uploadReason = useAppSelector((state) => state.upload.uploadReason);
	const canCreateOp = useAppSelector((state) => state.tab.userRoles).includes('CREATE_OP');
	const useCaseConfirmed = useAppSelector((state) => state.upload.useCaseConfirmed);
	const fileName = useAppSelector((state) => state.upload.currentFileName);
	const uploadComplete = useAppSelector((state) => getUploadComplete(state));

	const behalfOfInvalid =
		uploadReason.onBehalfOfName?.length === 0 ||
		uploadReason.onBehalfOfEmail?.length === 0 ||
		uploadReason.onBehalfOfName === undefined ||
		uploadReason.onBehalfOfEmail === undefined ||
		!uploadReason.onBehalfOfEmail?.endsWith('police.uk');

	const getOperationName = (): string => {
		if (selectedOperation.endsWith('-upload')) {
			return removeOpSuffix(selectedOperation).toLocaleLowerCase();
		} else {
			return selectedOperation.toLocaleLowerCase();
		}
	};

	const getDisabledReason = (): string => {
		if (!selectedOperation) {
			return 'An operation must be selected before a file can be uploaded';
		} else if (!noSuspect && !savedSuspect) {
			return 'A suspect name must be entered before a file can be uploaded';
		} else if (uploadReason.reason.length < 5) {
			return 'The Reason for Processing must be given and must be more than 5 characters before a file can be uploaded';
		} else if (uploadReason.onBehalfOf && behalfOfInvalid) {
			return 'If uploading on behalf of another user, both their name and an email ending in police.uk must be filled out before a file can be uploaded';
		} else if (!useCaseConfirmed) {
			return 'You must select and confirm a use case before a file can be uploaded';
		} else {
			return '';
		}
	};

	// stepper beta features
	const { activeStep, previousActiveStepRef, continueButtonRef, backButtonRef, stepperEnabled, enableStepper } = useUploadFormStepper();

	const uploadReasonValid = Boolean(
		uploadReason.reason.length > 4 &&
		(!uploadReason.onBehalfOf ||
			(uploadReason.onBehalfOf &&
				uploadReason.onBehalfOfEmail !== '' &&
				uploadReason.onBehalfOfName !== '' &&
				uploadReason.onBehalfOfEmail?.endsWith('police.uk')))
	);

	useEffect(() => {
		const previousActiveStep = previousActiveStepRef.current;
		previousActiveStepRef.current = activeStep;

		if (previousActiveStep < activeStep) {
			continueButtonRef.current?.focus();
		}

		backButtonRef.current?.focus();
	}, [activeStep]);

	useEffect(() => {
		if (featureFlags?.includes(FeatureFlag.NEW_UPLOAD_FORM)) {
			enableStepper(true);
		}
	}, []);

	if (stepperEnabled) {
		return (
			<Stack
				sx={{
					width: '100%',
				}}
				spacing={2}
				data-testid={testTag}
			>
				<Alert
					severity="info"
					action={
						<Button size="small" endIcon={<ArrowForward />} variant="outlined" onClick={() => dispatch(switchTab('Help.Feedback'))}>
							Feedback
						</Button>
					}
					sx={{ flex: 1, justifyContent: 'center' }}
				>
					{`You're seeing the refreshed upload form. Got feedback? Head to [Help] -> [Feedback] to send a message to the development team.`}
				</Alert>
				<Stepper activeStep={activeStep} orientation="vertical">
					<UploadFormStep
						key="opSelect"
						label="Select an Operation"
						valid={selectedOperation !== ''}
						validLabel={`Operation Selected (${removeOpSuffix(selectedOperation)})`}
						errorMessage="An operation must be selected"
					>
						<OperationDropdown
							selectedOption={selectedOperation}
							setSelectedOption={setSelectedOperation}
							operationType="UPLOAD"
							showCreateShortcut={canCreateOp}
						/>
					</UploadFormStep>
					<UploadFormStep
						key="deviceOwner"
						label="Specify Device Owner"
						valid={Boolean(savedSuspect && suspectType) || noSuspect === true}
						validLabel={`Device Owner Specified (${savedSuspect}) (${suspectType})`}
						errorMessage="A device owner and type must be selected"
					>
						<DeviceOwnerSelect forceDisabled={selectedOperation === ''} />
					</UploadFormStep>
					<UploadFormStep
						key="reasonForProcessing"
						label="Reason for Processing"
						valid={uploadReasonValid}
						validLabel={`Upload Reason Specified (${uploadReason.reason})`}
						errorMessage="A reason for processing of at least 5 characters must be specified"
					>
						<UploadReasonField forceDisabled={!(savedSuspect && suspectType)} />
					</UploadFormStep>
					<UploadFormStep
						key="useCase"
						label="Select and Confirm Use Case"
						valid={useCaseConfirmed}
						validLabel={'Use Case Confirmed'}
						errorMessage="A Use Case must be specified and confirmed"
					>
						<UseCaseSelector forceDisabled={!uploadReasonValid} />
					</UploadFormStep>
					<UploadFormStep
						label="Upload a File"
						validLabel={`File Uploaded (${fileName})`}
						valid={uploadComplete}
						key="fileUpload"
						errorMessage="A file must be uploaded and a file type specified"
					>
						<FileUpload
							allowedFormats={allowedFormats}
							forceDisabled={Boolean(getDisabledReason())}
							containerName={getOperationName()}
							disabledReason={getDisabledReason()}
							key={`${getOperationName()}-file-upload`}
						/>
					</UploadFormStep>
					{children}
				</Stepper>
				<SnackbarAlert
					severity="error"
					open={Boolean(loadError)}
					onClose={() => setLoadError('')}
					alertMessage={loadError}
					testId="load-error-snackbar"
				/>
			</Stack>
		);
	}

	return (
		<Stack
			sx={{
				width: '100%',
			}}
			spacing={2}
			data-testid={testTag}
		>
			{inDev && <DevWarning />}
			<OperationDropdown
				selectedOption={selectedOperation}
				setSelectedOption={setSelectedOperation}
				operationType="UPLOAD"
				showCreateShortcut={canCreateOp}
			/>
			<Collapse in={!selectedOperation}>
				<Alert data-testid="no-operation-alert" severity="info">
					An operation must be selected before continuing
				</Alert>
			</Collapse>
			{!noSuspect && <DeviceOwnerSelect forceDisabled={!selectedOperation} />}
			<Collapse in={Boolean(selectedOperation) && !noSuspect && (!savedSuspect || !suspectType)}>
				<Alert data-testid="no-device-owner-alert" severity="info">
					A device owner and type must be specified before continuing
				</Alert>
			</Collapse>
			{/* Some more work to be done here on simplifying verification, to be done with stepper feature */}
			<UploadReasonField forceDisabled={!selectedOperation || (!noSuspect && (!savedSuspect || !suspectType))} />
			<UseCaseSelector forceDisabled={!selectedOperation} />
			<FileUpload
				allowedFormats={allowedFormats}
				forceDisabled={Boolean(getDisabledReason())}
				containerName={getOperationName()}
				disabledReason={getDisabledReason()}
				key={`${getOperationName()}-file-upload`}
			/>
			{children}

			<SnackbarAlert
				severity="error"
				open={Boolean(loadError)}
				onClose={() => setLoadError('')}
				alertMessage={loadError}
				testId="load-error-snackbar"
			/>

			{/* TODO: there's some possible work here to move the process button + notify preference checkbox into the skeleton
				but this would require a rework of how onSubmit is handled across the various metadata components */}
		</Stack>
	);
};

export default UploadTabSkeleton;
